import { Check, Plus } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { useFavoriteColors } from '@/hooks/useFavoriteColors.tsx';

interface BackgroundColorPickerProps {
  color: string;
  onChange: (color: string) => void;
  /** When true, a 'code' badge is shown in the tooltip to indicate the color is set by scene_background() */
  overridden?: boolean;
}

export function BackgroundColorPicker({
  color,
  onChange,
  overridden = false,
}: BackgroundColorPickerProps) {
  const { favorites, addFavorite, removeFavorite, isFavorite } =
    useFavoriteColors();

  const alreadySaved = isFavorite(color);
  const canAdd = favorites.length < 16 && !alreadySaved;

  return (
    <Popover>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                className="relative h-5 w-5 rounded-full border border-white/30 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-1 focus:ring-offset-transparent"
                style={{ backgroundColor: color }}
                aria-label="Change background color"
              >
                {overridden && (
                  <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5 items-center justify-center rounded-full border border-sky-400/60 bg-background">
                    <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                  </span>
                )}
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="border-border bg-background text-foreground"
          >
            <p>
              Background Color
              {overridden && (
                <span className="ml-1.5 inline-block rounded border border-sky-500/30 px-1 font-mono text-[9px] leading-tight text-sky-500 dark:text-sky-400">
                  code
                </span>
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        side="top"
        align="center"
        className="w-auto rounded-md border-border bg-background p-4 shadow-md"
        style={{ minWidth: '200px' }}
        sideOffset={8}
      >
        <div className="flex flex-col gap-3">
          <HexColorPicker
            color={color}
            onChange={(newColor) => onChange(newColor.toUpperCase())}
            style={{ height: '120px', width: '100%' }}
          />

          {/* Favorites section */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Favorites</span>
              <button
                aria-label={
                  alreadySaved ? 'Already in favorites' : 'Add to favorites'
                }
                onClick={() => addFavorite(color)}
                disabled={!canAdd}
                className={`flex h-5 w-5 items-center justify-center rounded transition-colors duration-150 focus:outline-none ${
                  alreadySaved
                    ? 'cursor-default text-muted-foreground/40'
                    : canAdd
                      ? 'text-muted-foreground [@media(hover:hover)]:hover:bg-muted [@media(hover:hover)]:hover:text-foreground'
                      : 'cursor-not-allowed text-muted-foreground/40'
                }`}
              >
                {alreadySaved ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
              </button>
            </div>

            {favorites.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {favorites.map((fav) => (
                  <ContextMenu key={fav}>
                    <ContextMenuTrigger asChild>
                      <button
                        aria-label={`Select color ${fav}`}
                        onClick={() => onChange(fav)}
                        className={`h-6 w-6 flex-shrink-0 rounded-full shadow-sm ring-offset-background transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 [@media(hover:hover)]:hover:scale-110 ${fav === color.toUpperCase() ? 'ring-2 ring-ring ring-offset-1' : ''}`}
                        style={{ backgroundColor: fav }}
                      />
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      <ContextMenuItem
                        onSelect={() => removeFavorite(fav)}
                        className="text-destructive focus:text-destructive"
                      >
                        Remove from favorites
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground/60">
                No favorites yet. Pick a color and press +.
              </p>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
